import { GuidSanitizer } from "./GuidSanitizer";

export class LookupSanitizer {
    private _guidSanitizer: GuidSanitizer = new GuidSanitizer();
    /**
 * Sanitizes and standardizes a lookup value into a `ComponentFramework.EntityReference` format.
 *
 * @param {any} value - The lookup value object representing an entity reference, which can take on various formats based on the context (e.g., form, dataset).
 *
 * @example
 * // Example 1: ComponentFramework.EntityReference (with `etn` key)
 * const value1 = {
 *     etn: 'account',
 *     id: { guid: 'd93407d5-a9ec-4dd8-8f07-2f6c04b9e0e2' },
 *     name: 'Example Account'
 * };
 *
 * @example
 * // Example 2: ComponentFramework.LookupValue Format (with `entityType` key)
 * const value2 = {
 *     entityType: 'contact',
 *     id: 'f5a2d5b9-483d-4cb8-a61d-0ff7811c2f76',
 *     name: 'John Doe'
 * };
 *
 * @example
 * // Example 3: Dataset SetValue Format (with `entityName` key)
 * const value3 = {
 *     entityName: 'transactioncurrency',
 *     id: '9d565d95-8b61-eb11-a812-000d3adae901',
 *     name: 'USD'
 * };
 *
 */
    public getEntityReference(value: any): ComponentFramework.EntityReference {
        const keys = Object.keys(value);
        if (keys.includes('etn')) {
            return {
                ...value, id: {
                    guid: this._guidSanitizer.removeGuidBrackets(value.id.guid)
                }
            };
        }
        if (keys.includes('entityType')) {
            return {
                etn: value.entityType,
                id: {
                    guid: this._guidSanitizer.removeGuidBrackets(value.id)
                },
                name: value.name
            };
        }
        if (keys.includes('entityName')) {
            return {
                etn: value.entityName,
                id: {
                    guid: this._guidSanitizer.removeGuidBrackets(value.id)
                },
                name: value.name
            };
        }
        return value;

    }

    /**
 * Sanitizes and standardizes various lookup value formats into a `ComponentFramework.LookupValue` format.
 *
 * @param {any} value - The lookup value object representing an entity reference, which can take on various formats based on the context (e.g., form, dataset).
 *
 * @example
 * // Example 1: ComponentFramework.LookupValue Format (with `entityType` key)
 * const value1 = {
 *     entityType: 'contact',
 *     id: 'f5a2d5b9-483d-4cb8-a61d-0ff7811c2f76',
 *     name: 'John Doe'
 * };
 *
 * @example
 * // Example 2: Entity Reference Format (with `etn` key)
 * const value2 = {
 *     etn: 'account',
 *     id: { guid: 'd93407d5-a9ec-4dd8-8f07-2f6c04b9e0e2' },
 *     name: 'Example Account'
 * };
 *
 * @example
 * // Example 3: Dataset SetValue Format (with `entityName` key)
 * const value3 = {
 *     entityName: 'transactioncurrency',
 *     id: '9d565d95-8b61-eb11-a812-000d3adae901',
 *     name: 'USD'
 * };
 *
 */
    public getLookupValue(value: any): ComponentFramework.LookupValue {
        const keys = Object.keys(value);
        if (keys.includes('entityType')) {
            return { ...value, id: this._guidSanitizer.removeGuidBrackets(value.id) };
        }
        if (keys.includes('etn')) {
            return {
                entityType: value.etn,
                id: this._guidSanitizer.removeGuidBrackets(value.id.guid),
                name: value.name
            };
        }
        if (keys.includes('entityName')) {
            return {
                entityType: value.entityName,
                id: this._guidSanitizer.removeGuidBrackets(value.id),
                name: value.name
            };
        }
        return value;
    }
}